/**
 * Returns a document of the given DOM node.
 *
 * @param node - Source node.
 *
 * @returns Either an `ownerDocument`, or a node itself if it is a document.
 */
function nodeDocument(node) {
    return node.ownerDocument || node;
}

/**
 * Checks whether the given DOM node is an [element](https://developer.mozilla.org/en-US/docs/Web/API/Element).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `Element`, or `false` everything else.
 */
function isElementNode(node) {
    return node.nodeType === 1 /* Node.ELEMENT_NODE */;
}
/**
 * Checks whether the given DOM node is an [attribute](https://developer.mozilla.org/en-US/docs/Web/API/Attr).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `Attr`, or `false` everything else.
 */
function isAttributeNode(node) {
    return node.nodeType === 2 /* Node.ATTRIBUTE_NODE */;
}
/**
 * Checks whether the given DOM node is a [text](https://developer.mozilla.org/en-US/docs/Web/API/Text).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `Text`, or `false` everything else.
 */
function isTextNode(node) {
    return node.nodeType === 3 /* Node.TEXT_NODE */;
}
/**
 * Checks whether the given DOM node is a [CDATA section](https://developer.mozilla.org/en-US/docs/Web/API/CDATASection).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `CDATASection`, or `false` everything else.
 */
function isCDATASectionNode(node) {
    return node.nodeType === 4 /* Node.CDATA_SECTION_NODE */;
}
/**
 * Checks whether the given DOM node is a [processing instruction](https://developer.mozilla.org/en-US/docs/Web/API/ProcessingInstruction).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `ProcessingInstruction`, or `false` everything else.
 */
function isProcessingInstructionNode(node) {
    return node.nodeType === 7 /* Node.PROCESSING_INSTRUCTION_NODE */;
}
/**
 * Checks whether the given DOM node is a [comment](https://developer.mozilla.org/en-US/docs/Web/API/Comment).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `Comment`, or `false` everything else.
 */
function isCommentNode(node) {
    return node.nodeType === 8 /* Node.COMMENT_NODE */;
}
/**
 * Checks whether the given DOM node is a [document](https://developer.mozilla.org/en-US/docs/Web/API/Document).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `Document`, or `false` everything else.
 */
function isDocumentNode(node) {
    return node.nodeType === 9 /* Node.DOCUMENT_NODE */;
}
/**
 * Checks whether the given DOM node is a [document type](https://developer.mozilla.org/en-US/docs/Web/API/DocumentType).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `DocumentType`, or `false` everything else.
 */
function isDocumentTypeNode(node) {
    return node.nodeType === 10 /* Node.DOCUMENT_TYPE_NODE */;
}
/**
 * Checks whether the given DOM node is a [document fragment](https://developer.mozilla.org/en-US/docs/Web/API/DocumentFragment).
 *
 * @param node - A node to check.
 *
 * @returns `true` for `DocumentFragment`, or `false` everything else.
 */
function isDocumentFragmentNode(node) {
    return node.nodeType === 11 /* Node.DOCUMENT_FRAGMENT_NODE */;
}
/**
 * Checks whether the given DOM node is a [shadow root](https://developer.mozilla.org/en-US/docs/Web/API/ShadowRoot).
 *
 * Note that shadow root is a kind of {@link isDocumentFragmentNode document fragment}.
 *
 * @param node - A node to check.
 *
 * @returns `true` for `ShadowRoot`, or `false` everything else.
 */
function isShadowRootNode(node) {
    return isDocumentFragmentNode(node) && !!node.host;
}

function importNode(from, to, beforeOrImport, importContent = importNodeContent) {
    let before;
    if (typeof beforeOrImport === 'function') {
        importContent = beforeOrImport;
        before = null;
    }
    else {
        before = beforeOrImport || null;
    }
    const doc = nodeDocument(to);
    if (isElementNode(from)) {
        const elementClone = doc.createElement(from.tagName.toLowerCase());
        from.getAttributeNames().forEach(attr => elementClone.setAttribute(attr, from.getAttribute(attr)));
        importContent(from, elementClone);
        to.insertBefore(elementClone, before);
        return elementClone;
    }
    const nodeClone = doc.importNode(from, false);
    to.insertBefore(nodeClone, before);
    return nodeClone;
}
/**
 * Imports DOM node contents from one document to another.
 *
 * @param from - The node which contents to import.
 * @param to - The node to append imported nodes to.
 */
function importNodeContent(from, to) {
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < from.childNodes.length; ++i) {
        importNode(from.childNodes[i], to);
    }
}

/**
 * Finds a host element of the given DOM node. I.e. an enclosing element.
 *
 * Crosses shadow DOM bounds.
 *
 * @param node - Target DOM node.
 *
 * @returns Either parent element of the given node, or `undefined` when not found.
 */
function nodeHost(node) {
    const { parentNode } = node;
    return parentNode && isElementNode(parentNode) && parentNode
        || node.getRootNode().host // Inside shadow DOM?
        || undefined;
}

/**
 * Returns an owner window of the given DOM node.
 *
 * @param node - Source node.
 *
 * @returns Either a `defaultView` of the {@link nodeDocument node document}, or `window` instance if there is no one.
 */
function nodeWindow(node) {
    return nodeDocument(node).defaultView || window;
}

/**
 * Removes all child nodes from the given one.
 *
 * @param node - DOM node to remove the content of.
 */
function removeNodeContent(node) {
    node.textContent = null;
}

export { importNode, importNodeContent, isAttributeNode, isCDATASectionNode, isCommentNode, isDocumentFragmentNode, isDocumentNode, isDocumentTypeNode, isElementNode, isProcessingInstructionNode, isShadowRootNode, isTextNode, nodeDocument, nodeHost, nodeWindow, removeNodeContent };
//# sourceMappingURL=dom-primitives.js.map
